import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { getCookie } from "../components/Cookie.jsx";
import DataTable from "react-data-table-component";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";
import "../index.css";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#f1f4f9",
    },
  },
};
//************************************** */

// Flattening function for buses
const flattenBusData = (data) => {
  return data?.flatMap((item) =>
    item?.buses?.map((bus) => ({
      ...bus,
      src: item?.src,
      dest: item?.dest,
      doj: item?.doj,
      scrape_date: item?.scrape_date,
      batch_id: item?.batch_id,
      client_id: item?.client_id,
    }))
  );
};

// Flattening function for flights
const flattenFlightData = (data) => {
  return data?.flatMap((item) =>
    item?.flight_details?.map((flight) => ({
      ...flight,
      src: item.src,
      dest: item.dest,
      doj: item.doj,
      client_id: item.client_id,
      currency: item.currency,
    }))
  );
};

//hotel room data
const flattenHotelRoomData = (data) => {
  return data?.flatMap((item) =>
    item?.room_details?.map((hotelRoom) => ({
      ...hotelRoom,
      check_in: item.check_in,
      check_out: item.check_out,
      hotel_id: item.hotel_id,
      ota: item.ota,
    }))
  );
};

// Column definitions for blabla
const busColumns = [
  {
    id: 1,
    name: "Source",
    selector: (row) => row?.city_from_ota ?? "-",
    sortable: true,
  },
  {
    id: 2,
    name: "Destination",
    selector: (row) => row?.city_to_ota ?? "-",
    sortable: true,
  },
  { id: 3, name: "Currency", selector: (row) => row?.currency ?? "-", sortable: true },
  {
    id: 4,
    name: "Departure Date Local",
    selector: (row) => row?.departure_date_local ?? "-",
    sortable: true,
  },
  {
    id: 5,
    name: "Departure Time Local",
    selector: (row) => row?.departure_time_local ?? "-",
    sortable: true,
  },
  {
    id: 7,
    name: "Arrival Date Local",
    selector: (row) => row?.arrival_date_local ?? "-",
    sortable: true,
  },
  {
    id: 8,
    name: "Arrival Time Local",
    selector: (row) => row?.arrival_time_local ?? "-",
    sortable: true,
  },
  { id: 10, name: "Duration", selector: (row) => row?.duration ?? "-", sortable: true },
  {
    id: 11,
    name: "Stopovers Count",
    selector: (row) => row?.stopovers_count ?? 0,
    sortable: true,
  },
  {
    id: 12,
    name: "Seat Class",
    selector: (row) => row?.seat_class ?? "-",
    sortable: true,
  },
  {
    id: 13,
    name: "Seats Available",
    selector: (row) => row?.seats_available ?? 0,
    sortable: true,
  },
  {
    id: 14,
    name: "Carrier Name OTA",
    selector: (row) => row?.carrier_name_ota ?? "-",
    sortable: true,
    cell: (row) => (
      <Tooltip title={row?.carrier_name_ota ?? "-"} arrow>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          }}
        >
          {row?.carrier_name_ota ?? "-"}
        </div>
      </Tooltip>
    ),
  },
  {
    id: 15,
    name: "Ticket Price",
    selector: (row) => row?.ticket_price ?? 0,
    sortable: true,
  },
  {
    id: 16,
    name: "Ticket Discount",
    selector: (row) => row?.ticket_discount ?? 0,
    sortable: true,
  },
];

// Column definitions for flights
const flightColumns = [
  { id: 1, name: "Source", selector: (row) => row?.src ?? "-", sortable: true },
  { id: 2, name: "Currency", selector: (row) => row?.currency ?? "-", sortable: true },
  { id: 3, name: "Destination", selector: (row) => row?.dest ?? "-", sortable: true },
  {
    id: 4,
    name: (
      <div className="whitespace-normal break-words text-center">
        Date of Journey
      </div>
    ),
    selector: (row) => row?.doj ?? "-",
    sortable: true,
  },
  { id: 5, name: "Layover", selector: (row) => row?.layover ?? "-", sortable: true },
  {
    id: 6,
    name: "Fare Class",
    selector: (row) => row?.fare_class ?? "-",
    sortable: true,
  },
  {
    id: 7,
    name: "Fare",
    selector: (row) => row?.fare ?? 0,
    sortable: true,
  },
  {
    id: 8,
    name: "Base Fare",
    selector: (row) => row?.base_fare ?? 0,
    sortable: true,
  },
  {
    id: 9,
    name: "Surcharge",
    selector: (row) => row?.surcharge ?? 0,
    sortable: true,
  },
  {
    id: 10,
    name: "Journey Key",
    selector: (row) => row?.journey_key ?? "-",
    sortable: true,
  },
  {
    id: 11,
    name: "Duration (Minutes)",
    selector: (row) => row?.duration_minutes ?? 0,
    sortable: true,
  },
  {
    id: 12,
    name: "Departure Time",
    selector: (row) => row?.dep_time ?? "-",
    sortable: true,
  },
  {
    id: 13,
    name: "Arrival Time",
    selector: (row) => row?.arr_time ?? "-",
    sortable: true,
  },
];

const hotelRoomColumns = [
  {
    id: 1,
    name: "Check-In Date",
    selector: (row) => row?.check_in ?? "-",
    sortable: true,
  },
  {
    id: 2,
    name: "Check-Out Date",
    selector: (row) => row?.check_out ?? "-",
    sortable: true,
  },
  {
    id: 3,
    name: "Hotel ID",
    selector: (row) => row?.hotel_id ?? "-",
    sortable: true,
    cell: (row) => (
      <Tooltip title={row?.hotel_id ?? "-"} arrow>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          }}
        >
          {row?.hotel_id ?? "-"}
        </div>
      </Tooltip>
    ),
  },
  {
    id: 4,
    name: "OTA",
    selector: (row) => row?.ota ?? "-",
    sortable: true,
  },
  {
    id: 5,
    name: "ECID",
    selector: (row) => row?.ecid ?? "-",
    sortable: true,
  },
  {
    id: 6,
    name: "Room Type",
    selector: (row) => row?.room_type ?? "-",
    sortable: true,
    cell: (row) => (
      <Tooltip title={row?.room_type ?? "-"} arrow>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          }}
        >
          {row?.room_type ?? "-"}
        </div>
      </Tooltip>
    ),
  },
  {
    id: 7,
    name: "Discount",
    selector: (row) => row?.discount ?? 0,
    sortable: true,
  },
  {
    id: 8,
    name: "Coupon Code",
    selector: (row) => row?.coupon_code ?? "-",
    sortable: true,
  },
  {
    id: 9,
    name: "Tax",
    selector: (row) => row?.tax ?? 0,
    sortable: true,
  },
  {
    id: 10,
    name: "MRP",
    selector: (row) => row?.mrp ?? 0,
    sortable: true,
  },
  {
    id: 11,
    name: "Base Price",
    selector: (row) => row?.base_price ?? 0,
    sortable: true,
  },
  {
    id: 12,
    name: "Sold Out",
    selector: (row) => row?.is_sold_out ?? false,
    sortable: true,
  },
  {
    id: 13,
    name: "Description",
    selector: (row) => row?.description ?? "-",
    sortable: true,
  },
  {
    id: 14,
    name: "Room Key",
    selector: (row) => row?.room_key ?? "-",
    sortable: true,
  },
  {
    id: 15,
    name: "Bed Type",
    selector: (row) => row?.bed_type ?? "-",
    sortable: true,
  },
  {
    id: 16,
    name: "Room Identifier",
    selector: (row) => row?.room_identifier?.[0] ?? "-",
    sortable: true,
    cell: (row) => {
      const identifiers = row?.room_identifier ?? [];
      return (
        <Tooltip title={identifiers.length ? identifiers.join(", ") : "-"} arrow>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "150px",
            }}
          >
            {identifiers.length ? identifiers.join(", ") : "-"}
          </div>
        </Tooltip>
      );
    },
  },
];

// const hotelCatColumns = [];
const hotelCatColumns = [
  {
    id: 1,
    name: "Hotel Name",
    selector: (row) => row?.hotel_name ?? "-",
    sortable: true,
  },
  {
    id: 2,
    name: "Hotel ID",
    selector: (row) => row?.hotel_id ?? "-",
    sortable: true,
  },
  {
    id: 3,
    name: "Address",
    selector: (row) => row?.address ?? "-",
    sortable: true,
  },
  {
    id: 4,
    name: "City",
    selector: (row) => row?.city ?? "-",
    sortable: true,
  },
  {
    id: 5,
    name: "Category",
    selector: (row) => row?.category ?? "-",
    sortable: true,
  },
  {
    id: 6,
    name: "Check-In Time",
    selector: (row) => row?.check_in_time ?? "-",
    sortable: true,
  },
  {
    id: 7,
    name: "Check-Out Time",
    selector: (row) => row?.check_out_time ?? "-",
    sortable: true,
  },
  {
    id: 9,
    name: "Base Price",
    selector: (row) => row?.base_price ?? 0,
    sortable: true,
    right: true,
  },
  {
    id: 10,
    name: "MRP",
    selector: (row) => row?.mrp ?? 0,
    sortable: true,
  },
  {
    id: 11,
    name: "Discount",
    selector: (row) => row?.discount ?? 0,
    sortable: true,
  },
  {
    id: 12,
    name: "Tax",
    selector: (row) => row?.tax ?? 0,
    sortable: true,
  },
  {
    id: 13,
    name: "OTA",
    selector: (row) => row?.ota ?? "-",
    sortable: true,
  },
  {
    id: 14,
    name: "Rating",
    selector: (row) => row?.rating ?? 0,
    sortable: true,
  },
  {
    id: 15,
    name: "Reviews",
    selector: (row) => row?.reviews ?? 0,
    sortable: true,
  },
  {
    id: 16,
    name: "Hotel Distance",
    selector: (row) => row?.hotel_distance ?? "-",
    sortable: true,
  },
  {
    id: 17,
    name: "Scrape Date",
    selector: (row) => row?.scrape_date ?? "-",
    sortable: true,
  },
];


//redbusCatColumn

const redbusCatColumns = [
  {
    id: 1,
    name: "Date of Journey",
    selector: (row) => row?.doj ?? "-",
    sortable: true,
  },
  {
    id: 2,
    name: "Operator",
    selector: (row) => row?.operator ?? "-",
    sortable: true,
    cell: (row) => (
      <Tooltip title={row?.operator ?? "-"} arrow>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          }}
        >
          {row?.operator ?? "-"}
        </div>
      </Tooltip>
    ),
  },
  {
    id: 3,
    name: "Max Price",
    selector: (row) => row?.max_price ?? 0,
    sortable: true,
  },
  {
    id: 4,
    name: "Min Price",
    selector: (row) => row?.min_price ?? 0,
    sortable: true,
  },
  {
    id: 5,
    name: "Route ID",
    selector: (row) => row?.route_id ?? "-",
    sortable: true,
  },
  {
    id: 6,
    name: "Source",
    selector: (row) => row?.src_name ?? "-",
    sortable: true,
  },
  {
    id: 7,
    name: "Destination",
    selector: (row) => row?.dest_name ?? "-",
    sortable: true,
  },
  {
    id: 8,
    name: "Bus Type",
    selector: (row) => row?.bus_type ?? "-",
    sortable: true,
    cell: (row) => (
      <Tooltip title={row?.bus_type ?? "-"} arrow>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          }}
        >
          {row?.bus_type ?? "-"}
        </div>
      </Tooltip>
    ),
  },
  {
    id: 9,
    name: "Departure Time",
    selector: (row) => row?.dep_time ?? "-",
    sortable: true,
  },
  {
    id: 10,
    name: "Available Seats",
    selector: (row) => row?.available_seats ?? 0,
    sortable: true,
  },
  {
    id: 11,
    name: "Total Seats",
    selector: (row) => row?.total_seats ?? 0,
    sortable: true,
  },
  {
    id: 12,
    name: "Rating",
    selector: (row) => row?.rating ?? 0,
    sortable: true,
  },
  {
    id: 13,
    name: "Rating Count",
    selector: (row) => row?.rating_count ?? 0,
    sortable: true,
  },
  {
    id: 14,
    name: "Bus Status",
    selector: (row) => row?.bus_status ?? "-",
    sortable: true,
  },
  {
    id: 15,
    name: "Bus Score",
    selector: (row) => row?.bus_score ?? 0,
    sortable: true,
  },
  {
    id: 16,
    name: "Trip Duration (min)",
    selector: (row) => row?.trip_duration ?? 0,
    sortable: true,
  },
  {
    id: 17,
    name: "Boarding Points",
    selector: (row) => row?.boarding_points ?? "-",
    sortable: true,
  },
  {
    id: 18,
    name: "Dropping Points",
    selector: (row) => row?.dropping_points ?? "-",
    sortable: true,
  },
  {
    id: 19,
    name: "No of Stops",
    selector: (row) => row?.no_of_stops ?? 0,
    sortable: true,
  },
  {
    id: 20,
    name: "Last Scrape Date",
    selector: (row) => row?.last_scrape_date ?? "-",
    sortable: true,
  },
  {
    id: 21,
    name: "Time Tag",
    selector: (row) => row?.time_tag ?? "-",
    sortable: true,
  },
];

const redbusSeatColumns = [
  {
    id: 1,
    name: "Seat No",
    selector: (row) => row?.seat_no ?? "-",
    sortable: true,
  },
  {
    id: 2,
    name: "Available",
    selector: (row) => row?.available ?? "-",
    sortable: true,
  },
  {
    id: 3,
    name: "Amount",
    selector: (row) => row?.amount ?? 0,
    sortable: true,
  },
  {
    id: 4,
    name: "Basic Fare",
    selector: (row) => row?.basic_fare ?? 0,
    sortable: true,
  },
  // {
  //   id: 5,
  //   name: "Discount Amount",
  //   selector: (row) => row?.disc_amt ?? 0,
  //   sortable: true,
  //   cell: (row) => (
  //     <div className="whitespace-normal break-word margin">row?.disc_amt ?? 0,</div>
  //   ),
  // },
  {
    id: 6,
    name: "List Fare",
    selector: (row) => row?.list_fare ?? 0,
    sortable: true,
  },
  {
    id: 7,
    name: "Ladies Seat",
    selector: (row) => (row?.is_ladies ? "Yes" : "No"),
    sortable: true,
  },
  {
    id: 8,
    name: "Height",
    selector: (row) => row?.height ?? "-",
    sortable: true,
  },
  {
    id: 9,
    name: "Width",
    selector: (row) => row?.width ?? "-",
    sortable: true,
  },
  {
    id: 10,
    name: "Coordinates",
    selector: (row) => `X:${row?.x}, Y:${row?.y}, Z:${row?.z}`,
    sortable: false,
  },
  {
    id: 11,
    name: "Route ID",
    selector: (row) => row?.route_id ?? "-",
    sortable: true,
  },
  {
    id: 12,
    name: "Operator ID",
    selector: (row) => row?.operator_id ?? "-",
    sortable: true,
  },
  {
    id: 13,
    name: "Date of Journey",
    selector: (row) => row?.doj ?? "-",
    sortable: true,
  },
];



//***************************************** */
const Carousel = ({
  data,
  selectedImageUrl,
  selectedImageData,
  onSlideChange,
  currentSlideIndex,
}) => {
  const [currentImageData, setCurrentImageData] = useState([]);
  const [storedData, setStoredData] = useState([]);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [isBusDropdownOpen, setBusDropdownOpen] = useState(false);
  const [isFlightDropdownOpen, setFlightDropdownOpen] = useState(false);
  const [isHotelRoomDropdownOpen, setHotelRoomDropdownOpen] = useState(false);
  const [isHotelCatDropdownOpen, setHotelCatDropdownOpen] = useState(false);
  const [isRedbusCatDropdownOpen, setRedbusCatDropdownOpen] = useState(false);
  const [isRedbusSeatDropdownOpen, setRedbusSeatDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dataType, setDataType] = useState(""); // Default data type
  const [isOpen, setIsOpen] = useState(false);
  const sliderRef = useRef(null);
  const token = getCookie("sqa_token");

  const imageData = Array.isArray(data)
    ? data.filter((doc) => doc.screenshot_url !== null && doc.file_name)
    : [];

  const images = Array.isArray(imageData)
    ? imageData.map((doc) => doc.screenshot_url)
    : [];

  // Set the default selectedImageData based on the currentSlideIndex on mount
  useEffect(() => {
    if (imageData && imageData.length > 0) {
      setCurrentImageData(imageData[currentSlideIndex] || imageData[0]);
      setDataType(imageData[currentSlideIndex] || imageData[0]);
    }
  }, [imageData, currentSlideIndex]);

  // Fetch API with selectedImageData on mount
  useEffect(() => {
    const fetchData = async () => {
      if (currentImageData && currentImageData.length != 0) {
        try {
          const response = await fetch("/sqa/api/ss_image_data/", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`, // Include token if required
            },
            body: JSON.stringify({
              nextImageData: currentImageData, // Sending selectedImageData as payload
            }),
          });

          if (!response.ok) {
            throw new Error("Network response was not ok");
          }

          const responseData = await response.json();

          if (responseData.documents && responseData.documents.length > 0) {
            setStoredData(responseData.documents);
          } else {
            setStoredData([]);
          }
        } catch (error) {
          console.error("Error making API call:", error);
          setStoredData([]);
        }
      }
    };

    fetchData();
  }, [currentImageData]); // Only run when selectedImageData changes

  // Update the selected slide when selectedImageUrl changes
  useEffect(() => {
    if (selectedImageUrl && sliderRef.current) {
      const index = images.indexOf(selectedImageUrl);
      if (index !== -1) {
        sliderRef.current.slickGoTo(index);
        setCurrentImageData(imageData[index]);
      }
    }
  }, [selectedImageUrl, images, imageData]);

  const renderHeader = (name) => (
    <div className="whitespace-normal break-words text-center font-bold text-sm ">
      {name}
    </div>
  );

  const filteredData = (() => {
   
    
    let dataToFilter;
    // Choose data based on the type value
    if (dataType?.batch_id?.toLowerCase().includes("redbus$cat")) {
      dataToFilter = storedData;
    } else if (dataType?.batch_id?.toLowerCase().includes("redbus$seat")) {
      dataToFilter = storedData;
    } else if (dataType?.site_id?.toLowerCase() == "paytmflight") {
      dataToFilter = flattenFlightData(storedData);
    } else if (dataType?.batch_id?.toLowerCase().includes("hotel$room")) {
      dataToFilter = flattenHotelRoomData(storedData);
    } else if (dataType?.batch_id?.toLowerCase().includes("hotel$cat")) {
      dataToFilter = storedData;
    } else if (dataType?.client_id?.toLowerCase() == "blablacar") {
      dataToFilter = flattenBusData(storedData);
    }

    return (
      dataToFilter &&
      dataToFilter.filter((item) =>
        Object.values(item).some((value) =>
          value?.toString()?.toLowerCase()?.includes(searchQuery.toLowerCase())
        )
      )
    );
  })();

  // const filteredData =
  //   storedData &&
  //   storedData?.filter((item) =>
  //     Object.values(item).some((value) =>
  //       value.toString().toLowerCase().includes(searchQuery.toLowerCase())
  //     )
  //   );

  const toggleDropdown = (type) => {

    
    setIsOpen(!isOpen);
    if (type === "blablacar") {
      setBusDropdownOpen(!isBusDropdownOpen);
    } else if (type == "flights") {
      setFlightDropdownOpen(!isFlightDropdownOpen);
    } else if (type == "hotelRoom") {
      setHotelRoomDropdownOpen(!isHotelRoomDropdownOpen);
    } else if (type == "hotelCat") {
      setHotelCatDropdownOpen(!isHotelCatDropdownOpen);
    } else if (type == "redbusCat") {
      setRedbusCatDropdownOpen(!isRedbusCatDropdownOpen);
    } else if (type == "redbusSeat") {
      setRedbusSeatDropdownOpen(!isRedbusSeatDropdownOpen);
    }
  };

  // const toggleDropdown = (type) => {
  //   setIsOpen((prev) => !prev);

  //   if (type === "blablacar") {
  //     setBusDropdownOpen(false);
  //     setFlightDropdownOpen(false);
  //     setHotelRoomDropdownOpen(false);
  //     setHotelCatDropdownOpen(false);
  //     setRedbusCatDropdownOpen(false);
  //     setBusDropdownOpen((prev) => !prev);
  //   } else if (type === "flights") {
  //     setBusDropdownOpen(false);
  //     setHotelRoomDropdownOpen(false);
  //     setHotelCatDropdownOpen(false);
  //     setRedbusCatDropdownOpen(false);
  //     setFlightDropdownOpen((prev) => !prev);
  //   } else if (type === "hotelRoom") {
  //     setBusDropdownOpen(false);
  //     setFlightDropdownOpen(false);
  //     setHotelCatDropdownOpen(false);
  //     setRedbusCatDropdownOpen(false);
  //     setHotelRoomDropdownOpen((prev) => !prev);
  //   } else if (type === "hotelCat") {
  //     setBusDropdownOpen(false);
  //     setFlightDropdownOpen(false);
  //     setHotelRoomDropdownOpen(false);
  //     setRedbusCatDropdownOpen(false);
  //     setHotelCatDropdownOpen((prev) => !prev);
  //   } else if (type === "redbusCat") {
  //     setBusDropdownOpen(false);
  //     setFlightDropdownOpen(false);
  //     setHotelRoomDropdownOpen(false);
  //     setHotelCatDropdownOpen(false);
  //     setRedbusCatDropdownOpen((prev) => !prev);
  //   }
  // };

  //blablacar
  const [busColumnVisibility, setBusColumnVisibility] = useState({
    Source: true,
    Destination: true,
    Currency: true,
    Company: true,
    "Departure Date Local": true,
    Price: true,
    "Carrier Name OTA": true,
    "Arrival Time": true,
    "Ticket Price": true,
  });

  const [flightColumnVisibility, setFlightColumnVisibility] = useState({
    Source: true,
    Currency: true,
    Destination: true,
    "Date of Journey": true,
    // Layover: true,
    // "Fare Class": true,
    Fare: true,
    "Base Fare": true,
    Surcharge: true,
    // "Journey Key": true,
    "Duration (Minutes)": true,
    "Departure Time": true,
    "Arrival Time": true,
  });

  //room hotel
  const [hotelRoomColumnVisibility, setHotelRoomColumnVisibility] = useState({
    "Check-In Date": true,
    "Check-Out Date": true,
    "Hotel ID": true,
    OTA: true,
    "Room Type": true,
    Discount: true,
    Tax: true,
    MRP: true,
    "Base Price": true,
    // "Sold Out": true,
    // Description: true,
    // "Room Key": true,
    "Bed Type": true,
    "Room Identifier": true,
  });

  //hotel cat
  const [hotelCatColumnVisibility, setHotelCatColumnVisibility] = useState({
    "Hotel Name": true,
    "Hotel ID": true,
    Address: true,
    City: true,
    Category: true,
    "Check-In Time": true,
    "Check-Out Time": true,
    "Base Price": true,
    MRP: true,
    Discount: true,
    Tax: true,
    OTA: true,
    Rating: true,
    Reviews: true,
    "Hotel Distance": true,
    "Scrape Date": true,
  });

  //redbus cat

  const [redBusCatColumnVisibility, setRedbusCatColumnVisibility] = useState({
    "Date of Journey": true,
    "Route ID": true,
    Source: true,
    Destination: true,
    "Bus Type": true,
    "Departure Time": true,
    "Available Seats": true,
    "Total Seats": true,
    Rating: true,
    "Rating Count": true,
    "Bus Status": true,
    "Bus Score": true,
    "Trip Duration (min)": true,
    "Boarding Points": true,
    "Dropping Points": true,
    "No of Stops": true,
    "Last Scrape Date": true,
    "Time Tag": true,
    Operator: true,
    "Max Price": true,
    "Min Price": true,
  });

  //redbusSeat

  const [redbusSeatColumnVisibility, setRedbusSeatColumnVisibility] = useState({
    "Seat No": true,
    Available: true,
    Amount: true,
    "Basic Fare": true,
    "Discount Amount": true,
    "List Fare": true,
    "Ladies Seat": true,
    Height: false,
    Width: false,
    Coordinates: false,
    "Route ID": true,
    "Operator ID": false,
    "Date of Journey": true,
  });
  

  //blablacar
  const toggleBusColumn = (columnName) => {
    setBusColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };
  //flight
  const toggleFlightColumn = (columnName) => {
    setFlightColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  //
  const toggleHotelRoom = (columnName) => {
    setHotelRoomColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  //hotel cat
  const toggleHotelCat = (columnName) => {
    setHotelCatColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  //redbus cat
  const toggleRedbusCat = (columnName) => {
    setRedbusCatColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  //redbus seat
  const toggleRedbusSeat = (columnName) => {
    setRedbusSeatColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  const visibleBusColumns = busColumns?.filter(
    (column) => busColumnVisibility[column.name]
  );

  const visibleFlightColumns = flightColumns?.filter(
    (column) => flightColumnVisibility[column.name]
  );

  //hotel room
  const visibleHotelRoomColumn = hotelRoomColumns?.filter(
    (column) => hotelRoomColumnVisibility[column.name]
  );

  //hotelCat
  const visibleHotelCatColumn = hotelCatColumns?.filter(
    (column) => hotelCatColumnVisibility[column.name]
  );

  //redbus
  const visibleRedbusCatColumn = redbusCatColumns?.filter(
    (column) => redBusCatColumnVisibility[column.name]
  );

  const visibleRedbusSeatColumn = redbusSeatColumns?.filter(
    (column) => redbusSeatColumnVisibility[column.name]
  );

  const settings = {
    // dots: images.length > 1,
    lazyLoad: true,
    infinite: images.length > 1,
    speed: 500,
    autoplay: false,
    autoplaySpeed: 5000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows:false,
    // nextArrow: <ArrowRightIcon />,
    // prevArrow: <ArrowLeftIcon />,
    ref: sliderRef,
    beforeChange: async (current, next) => {
      const nextImageData = imageData[next];
      setCurrentImageData(nextImageData);

      // Check if the values exist before making the API call
      // try {
      //   const response = await fetch("/sqa/api/ss_image_data/", {
      //     method: "POST",
      //     headers: {
      //       "Content-Type": "application/json",
      //       Authorization: `Bearer ${token}`, // Include token if required
      //     },
      //     body: JSON.stringify({
      //       nextImageData,
      //     }),
      //   });

      //   if (!response.ok) {
      //     throw new Error("Network response was not ok");
      //   }

      //   const responseData = await response.json();

      //   if (responseData.documents && responseData.documents.length > 0) {
      //     setStoredData(responseData.documents);
      //   } else {
      //     setStoredData([]);
      //   }
      // } catch (error) {
      //   console.error("Error making API call:", error);
      //   setStoredData([]);
      // }

      if (onSlideChange) {
        onSlideChange(next);
      }
    },
  };

  return (
    <>
      <Slider {...settings} ref={sliderRef} className="custom-height-carousel">
        {images.length > 0 ? (
          images.map((url, index) => (
            <div key={index} className="text-center">
              <h3 className="text-lg font-bold mb-2">
                SS for Image{" "}
                <span className="text-sm font-normal">
                  (click to download image)
                </span>
              </h3>
              <img
                src={url}
                alt="No image"
                className="w-full h-[10rem] object-cover rounded-xl cursor-pointer"
                loading="lazy" // Add lazy loading
                onClick={() => window.open(url, "_blank")}
              />
              <div>
                <div className="w-full text-left">
                  <>
                    {dataType?.client_id?.toLowerCase() == "blablacar" && (
                      <>
                        {storedData?.length != 0 && (
                          <div className="flex items-center justify-between py-10">
                            <div className="relative inline-block text-left">
                              {/* Aligns button to left */}
                              <button
                                onClick={() => toggleDropdown("blablacar")}
                                className="bg-gray-200 px-4 py-2 border border-gray-300 rounded-md w-full text-left"
                              >
                                Select BlaBlaCar Columns
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </button>
                              {isBusDropdownOpen && (
                                <div className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-lg p-2 mt-1 w-48 max-h-48 overflow-y-auto">
                                  {busColumns.map((column) => (
                                    <label
                                      key={column.id}
                                      className="flex items-center py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          busColumnVisibility[column.name]
                                        }
                                        onChange={() =>
                                          toggleBusColumn(column.name)
                                        }
                                        className="mr-2"
                                      />
                                      {column.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="w-1/3">
                              <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border rounded-md px-4 py-2 w-full"
                              />
                            </div>
                          </div>
                        )}
                        <DataTable
                          columns={visibleBusColumns.map((column) => ({
                            ...column,
                            name: renderHeader(column.name), // Render headers with bold styling
                          }))}
                          data={filteredData}
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="py-10">No Data Found</div>
                          }
                          pagination
                        />
                      </>
                    )}
                    {dataType?.site_id?.toLowerCase() == "paytmflight" && (
                      <>
                        {storedData?.length != 0 && (
                          <div className="flex items-center justify-between py-10">
                            <div className="relative inline-block text-left">
                              {/* Aligns button to left */}
                              <button
                                onClick={() => toggleDropdown("flights")}
                                className="bg-gray-200 px-4 py-2 border border-gray-300 rounded-md w-full text-left"
                              >
                                Select Flight Columns
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </button>
                              {isFlightDropdownOpen && (
                                <div className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-lg p-2 mt-1 w-48 max-h-48 overflow-y-auto">
                                  {flightColumns.map((column) => (
                                    <label
                                      key={column?.id}
                                      className="flex items-center py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          flightColumnVisibility[column?.name]
                                        }
                                        onChange={() =>
                                          toggleFlightColumn(column?.name)
                                        }
                                        className="mr-2"
                                      />
                                      {column?.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="w-1/3">
                              <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border rounded-md px-4 py-2 w-full"
                              />
                            </div>
                          </div>
                        )}

                        <DataTable
                          columns={visibleFlightColumns.map((column) => ({
                            ...column,
                            name: renderHeader(column.name), // Render headers with bold styling
                          }))}
                          data={filteredData}
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="py-10">No Data Found</div>
                          }
                          pagination
                        />
                      </>
                    )}
                    {dataType?.batch_id
                      ?.toLowerCase()
                      .includes("hotel$room") && (
                      <>
                        {storedData?.length != 0 && (
                          <div className="flex items-center justify-between py-10">
                            <div className="relative inline-block text-left">
                              {/* Aligns button to left */}
                              <button
                                onClick={() => toggleDropdown("hotelRoom")}
                                className="bg-gray-200 px-4 py-2 border border-gray-300 rounded-md w-full text-left flex justify-between items-center"
                              >
                                Select Hotel Room Columns
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </button>
                              {isHotelRoomDropdownOpen && (
                                <div className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-lg p-2 mt-1 w-48 max-h-48 overflow-y-auto">
                                  {hotelRoomColumns.map((column) => (
                                    <label
                                      key={column?.id}
                                      className="flex items-center py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          hotelRoomColumnVisibility[
                                            column?.name
                                          ]
                                        }
                                        onChange={() =>
                                          toggleHotelRoom(column?.name)
                                        }
                                        className="mr-2"
                                      />
                                      {column?.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="w-1/3">
                              <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border rounded-md px-4 py-2 w-full"
                              />
                            </div>
                          </div>
                        )}
                        <DataTable
                          columns={visibleHotelRoomColumn.map((column) => ({
                            ...column,
                            name: renderHeader(column.name), // Render headers with bold styling
                          }))}
                          data={filteredData}
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="py-10">No Data Found</div>
                          }
                          pagination
                        />
                      </>
                    )}
                    {dataType?.batch_id
                      ?.toLowerCase()
                      .includes("hotel$cat") && (
                      <>
                        {storedData?.length != 0 && (
                          <div className="flex items-center justify-between py-10">
                            <div className="relative inline-block text-left">
                              {/* Aligns button to left */}
                              <button
                                onClick={() => toggleDropdown("hotelCat")}
                                className="bg-gray-200 px-4 py-2 border border-gray-300 rounded-md w-full text-left flex justify-between items-center"
                              >
                                Select Hotel Columns
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </button>
                              {isHotelCatDropdownOpen && (
                                <div className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-lg p-2 mt-1 w-48 max-h-48 overflow-y-auto">
                                  {hotelCatColumns.map((column) => (
                                    <label
                                      key={column?.id}
                                      className="flex items-center py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          hotelCatColumnVisibility[column?.name]
                                        }
                                        onChange={() =>
                                          toggleHotelCat(column?.name)
                                        }
                                        className="mr-2"
                                      />
                                      {column?.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="w-1/3">
                              <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border rounded-md px-4 py-2 w-full"
                              />
                            </div>
                          </div>
                        )}

                        <DataTable
                          columns={visibleHotelCatColumn.map((column) => ({
                            ...column,
                            name: renderHeader(column.name), // Render headers with bold styling
                          }))}
                          data={filteredData}
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="py-10">No Data Found</div>
                          }
                          pagination
                        />
                      </>
                    )}
                    {dataType?.batch_id
                      ?.toLowerCase()
                      .includes("redbus$cat") && (
                      <>
                        {storedData?.length != 0 && (
                          <div className="flex items-center justify-between py-10">
                            <div className="relative inline-block text-left">
                              {/* Aligns button to left */}
                              <button
                                onClick={() => toggleDropdown("redbusCat")}
                                className="bg-gray-200 px-4 py-2 border border-gray-300 rounded-md w-full text-left flex justify-between items-center"
                              >
                                Select Redbus Cat Columns
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </button>
                              {isRedbusCatDropdownOpen && (
                                <div className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-lg p-2 mt-1 w-48 max-h-48 overflow-y-auto">
                                  {redbusCatColumns.map((column) => (
                                    <label
                                      key={column?.id}
                                      className="flex items-center py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          redBusCatColumnVisibility[
                                            column?.name
                                          ]
                                        }
                                        onChange={() =>
                                          toggleRedbusCat(column?.name)
                                        }
                                        className="mr-2"
                                      />
                                      {column?.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="w-1/3">
                              <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border rounded-md px-4 py-2 w-full"
                              />
                            </div>
                          </div>
                        )}

                        <DataTable
                          columns={visibleRedbusCatColumn.map((column) => ({
                            ...column,
                            name: renderHeader(column.name),
                          }))}
                          data={filteredData}
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="py-10">No Data Found</div>
                          }
                          pagination
                        />
                      </>
                    )}

                    {dataType?.batch_id
                      ?.toLowerCase()
                      .includes("redbus$seat") && (
                      <>
                        {storedData?.length != 0 && (
                          <div className="flex items-center justify-between py-10">
                            <div className="relative inline-block text-left">
                              {/* Aligns button to left */}
                              <button
                                onClick={() => toggleDropdown("redbusSeat")}
                                className="bg-gray-200 px-4 py-2 border border-gray-300 rounded-md w-full text-left flex justify-between items-center"
                              >
                                Select Redbus Seat Columns
                                {isOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </button>
                              {isRedbusSeatDropdownOpen && (
                                <div className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-lg p-2 mt-1 w-48 max-h-48 overflow-y-auto">
                                  {redbusSeatColumns.map((column) => (
                                    <label
                                      key={column?.id}
                                      className="flex items-center py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          redbusSeatColumnVisibility[
                                            column?.name
                                          ]
                                        }
                                        onChange={() =>
                                          toggleRedbusSeat(column?.name)
                                        }
                                        className="mr-2"
                                      />
                                      {column?.name}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="w-1/3">
                              <input
                                type="text"
                                placeholder="Search here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border rounded-md px-4 py-2 w-full"
                              />
                            </div>
                          </div>
                        )}

                        {console.log("filteredData+++++++++++==", filteredData)
                        }
                        
                        <DataTable
                          columns={visibleRedbusSeatColumn.map((column) => ({
                            ...column,
                            name: renderHeader(column.name),
                          }))}
                          data={filteredData}
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="py-10">No Data Found</div>
                          }
                          pagination
                        />
                      </>
                    )}
                  </>
                </div>
              </div>{" "}
              {/* Closing the div for the toggle and DataTable section */}
            </div> // Closing the div for each image item
          ))
        ) : (
          <div className="text-center">
            <p>No images available</p>
          </div>
        )}
      </Slider>
    </>
  );
};

export default Carousel;
